import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';

interface NavigationEvent {
  timestamp: number;
  path: string;
  userId?: string;
  sessionId: string;
  referrer?: string;
  courseId?: string;
  moduleId?: string;
}

// Simple analytics to track navigation patterns
class NavigationAnalytics {
  private static instance: NavigationAnalytics;
  private events: NavigationEvent[] = [];
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  static getInstance(): NavigationAnalytics {
    if (!NavigationAnalytics.instance) {
      NavigationAnalytics.instance = new NavigationAnalytics();
    }
    return NavigationAnalytics.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  trackNavigation(path: string, userId?: string, referrer?: string) {
    // Extract course and module IDs from path
    const pathSegments = path.split('/').filter(Boolean);
    let courseId: string | undefined;
    let moduleId: string | undefined;

    if (pathSegments.length >= 2 && pathSegments[0].endsWith('-courses')) {
      courseId = pathSegments[1];
      if (pathSegments.length >= 3) {
        moduleId = pathSegments[2];
      }
    }

    const event: NavigationEvent = {
      timestamp: Date.now(),
      path,
      userId,
      sessionId: this.sessionId,
      referrer,
      courseId,
      moduleId
    };

    this.events.push(event);

    // Keep only last 100 events to prevent memory bloat
    if (this.events.length > 100) {
      this.events = this.events.slice(-100);
    }

    // Store in localStorage for persistence across sessions
    try {
      const storedEvents = JSON.parse(localStorage.getItem('navigation_analytics') || '[]');
      storedEvents.push(event);
      
      // Keep only last 200 events in localStorage
      const recentEvents = storedEvents.slice(-200);
      localStorage.setItem('navigation_analytics', JSON.stringify(recentEvents));
    } catch (error) {
      console.warn('Failed to store navigation analytics:', error);
    }
  }

  getRecentPaths(userId?: string): string[] {
    const userEvents = userId 
      ? this.events.filter(e => e.userId === userId) 
      : this.events;
    
    return userEvents
      .slice(-10)
      .map(e => e.path)
      .filter((path, index, arr) => arr.indexOf(path) === index); // Remove duplicates
  }

  getMostVisitedCourses(userId?: string): Array<{ courseId: string; visits: number }> {
    const userEvents = userId 
      ? this.events.filter(e => e.userId === userId) 
      : this.events;
    
    const courseVisits: Record<string, number> = {};
    
    userEvents.forEach(event => {
      if (event.courseId) {
        courseVisits[event.courseId] = (courseVisits[event.courseId] || 0) + 1;
      }
    });

    return Object.entries(courseVisits)
      .map(([courseId, visits]) => ({ courseId, visits }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 5);
  }

  getSessionDuration(): number {
    if (this.events.length === 0) return 0;
    
    const firstEvent = this.events[0];
    const lastEvent = this.events[this.events.length - 1];
    
    return lastEvent.timestamp - firstEvent.timestamp;
  }

  // Get learning patterns for recommendations
  getLearningPatterns(userId?: string) {
    const userEvents = userId 
      ? this.events.filter(e => e.userId === userId) 
      : this.events;

    const patterns = {
      preferredTimeSpent: this.getSessionDuration(),
      mostActiveHours: this.getMostActiveHours(userEvents),
      courseProgression: this.getCourseProgression(userEvents),
      moduleEngagement: this.getModuleEngagement(userEvents)
    };

    return patterns;
  }

  private getMostActiveHours(events: NavigationEvent[]): number[] {
    const hourCounts: Record<number, number> = {};
    
    events.forEach(event => {
      const hour = new Date(event.timestamp).getHours();
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });

    return Object.entries(hourCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([hour]) => parseInt(hour));
  }

  private getCourseProgression(events: NavigationEvent[]): Array<{ courseId: string; lastAccessed: number }> {
    const courseAccess: Record<string, number> = {};
    
    events.forEach(event => {
      if (event.courseId) {
        courseAccess[event.courseId] = Math.max(
          courseAccess[event.courseId] || 0, 
          event.timestamp
        );
      }
    });

    return Object.entries(courseAccess)
      .map(([courseId, lastAccessed]) => ({ courseId, lastAccessed }))
      .sort((a, b) => b.lastAccessed - a.lastAccessed);
  }

  private getModuleEngagement(events: NavigationEvent[]): Array<{ moduleId: string; timeSpent: number }> {
    const moduleTimeSpent: Record<string, { start: number; total: number }> = {};
    
    events.forEach((event, index) => {
      if (event.moduleId) {
        if (!moduleTimeSpent[event.moduleId]) {
          moduleTimeSpent[event.moduleId] = { start: event.timestamp, total: 0 };
        }
        
        // Calculate time spent if there's a next event
        if (index < events.length - 1) {
          const nextEvent = events[index + 1];
          const timeSpent = nextEvent.timestamp - event.timestamp;
          
          // Only count reasonable time spans (less than 30 minutes)
          if (timeSpent < 30 * 60 * 1000) {
            moduleTimeSpent[event.moduleId].total += timeSpent;
          }
        }
      }
    });

    return Object.entries(moduleTimeSpent)
      .map(([moduleId, data]) => ({ moduleId, timeSpent: data.total }))
      .sort((a, b) => b.timeSpent - a.timeSpent)
      .slice(0, 10);
  }
}

// React hook for navigation tracking
export const useNavigationAnalytics = () => {
  const location = useLocation();
  const { user } = useSupabaseAuth();
  const analytics = NavigationAnalytics.getInstance();

  useEffect(() => {
    analytics.trackNavigation(
      location.pathname, 
      user?.id, 
      document.referrer
    );
  }, [location.pathname, user?.id]);

  return {
    getRecentPaths: () => analytics.getRecentPaths(user?.id),
    getMostVisitedCourses: () => analytics.getMostVisitedCourses(user?.id),
    getLearningPatterns: () => analytics.getLearningPatterns(user?.id),
    getSessionDuration: () => analytics.getSessionDuration()
  };
};

// Component for automatic tracking
const NavigationAnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useNavigationAnalytics();
  return <>{children}</>;
};

export default NavigationAnalyticsProvider;
